import type { FC } from "react"
import { useMemo, useState } from "react"

import { useGrCostBreakdownMutation } from "../hooks/useGrCostBreakdownMutation"
import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../schemas"
import { CostBreakdownDialogType } from "../types"
import { getCostBreakdownBaseFormValues } from "../utils/formDefaults"
import { transformFormDataToPayload } from "../utils/transformFormDataToPayload"
import { transformGoodsReceiptToFormData } from "../utils/transformGoodsReceiptToFormData"

import { CostBreakdownDialog } from "./CostBreakdownDialog"
import { CostBreakdownProductsDialog } from "./CostBreakdownProductsDialog"

import { CancelDialog } from "@/components/common/Modals"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"

interface CostBreakdownDialogsProps {
  goodsReceipt: GoodsReceipt | undefined
  purchaseOrderLineId?: string
  onClose: () => void
  onSuccess: () => void
}

export const CostBreakdownDialogs: FC<CostBreakdownDialogsProps> = ({
  goodsReceipt,
  purchaseOrderLineId,
  onClose,
  onSuccess,
}) => {
  const { upsertGrCostBreakdown, isLoading } = useGrCostBreakdownMutation({
    successMessage: "Cost breakdown has been successfully saved",
    errorMessage: "Failed to save cost breakdown. Please try again.",
  })

  // Determine edit mode based on presence of cost breakdown data
  const isEditMode = !!goodsReceipt?.costBreakdown

  // Track if user has made ANY changes during the entire dialog flow
  const [hasUserMadeAnyChanges, setHasUserMadeAnyChanges] = useState(false)

  // Initialize data for edit mode
  const { initialGrCostBreakdown, initialProductsCostBreakdown } = useMemo(() => {
    if (!isEditMode || !goodsReceipt?.costBreakdown) {
      return { initialGrCostBreakdown: undefined, initialProductsCostBreakdown: undefined }
    }

    try {
      const { grCostBreakdown, productsCostBreakdown } = transformGoodsReceiptToFormData(goodsReceipt)
      return {
        initialGrCostBreakdown: grCostBreakdown,
        initialProductsCostBreakdown: productsCostBreakdown,
      }
    } catch (error) {
      console.error("Failed to transform existing cost breakdown data:", error)
      return { initialGrCostBreakdown: undefined, initialProductsCostBreakdown: undefined }
    }
  }, [isEditMode, goodsReceipt])

  const [currentDialog, setCurrentDialog] = useState<CostBreakdownDialogType | null>(
    purchaseOrderLineId ? CostBreakdownDialogType.ProductsCostBreakdown : CostBreakdownDialogType.GrCostBreakdown
  )
  const [grCostBreakdown, setGrCostBreakdown] = useState<CostBreakdownFormValues | undefined>(
    purchaseOrderLineId && !initialGrCostBreakdown
      ? getCostBreakdownBaseFormValues(goodsReceipt)
      : initialGrCostBreakdown
  )
  const [productsCostBreakdown, setProductsCostBreakdown] = useState<CostBreakdownProductsFormValues | undefined>(
    initialProductsCostBreakdown
  )

  const handleCloseDialog = (isDirty: boolean, currentData?: CostBreakdownFormValues) => {
    if (isDirty) {
      setHasUserMadeAnyChanges(true)
    }

    if (isDirty || hasUserMadeAnyChanges) {
      if (currentData) {
        setGrCostBreakdown(currentData)
      }
      setCurrentDialog(CostBreakdownDialogType.Cancel)
    } else {
      handleCancelAllDialogs()
    }
  }

  const handleProductsCloseDialog = (isDirty: boolean, currentData?: CostBreakdownProductsFormValues) => {
    if (isDirty) {
      setHasUserMadeAnyChanges(true)
    }

    if (isDirty || hasUserMadeAnyChanges) {
      if (currentData) {
        setProductsCostBreakdown(currentData)
      }
      setCurrentDialog(CostBreakdownDialogType.Cancel)
    } else {
      handleCancelAllDialogs()
    }
  }

  const handleContinueToProducts = (data: CostBreakdownFormValues, formIsDirty: boolean) => {
    if (formIsDirty) {
      setHasUserMadeAnyChanges(true)
    }

    setGrCostBreakdown(data)

    if (isEditMode && formIsDirty) {
      const updatedProductsCostBreakdown = productsCostBreakdown
        ? {
            ...productsCostBreakdown,
            products: productsCostBreakdown.products.map((product) => ({
              ...product,
              allocatedGrCostPercentage: null,
              allocatedGrCostAmount: null,
              totalLaidDownCostPerUom: null,
            })),
          }
        : undefined
      setProductsCostBreakdown(updatedProductsCostBreakdown)
    }

    setCurrentDialog(CostBreakdownDialogType.ProductsCostBreakdown)
  }

  const handleBackToGrCostBreakdown = (currentProductsData?: CostBreakdownProductsFormValues) => {
    if (currentProductsData) {
      setProductsCostBreakdown(currentProductsData)
    }
    setCurrentDialog(CostBreakdownDialogType.GrCostBreakdown)
  }

  const handleFinalSubmit = async (
    grCostBreakdown: CostBreakdownFormValues,
    productsCostBreakdown: CostBreakdownProductsFormValues
  ) => {
    if (!goodsReceipt?.goodsReceiptId) {
      console.error("No goods receipt selected")
      return
    }

    const input = transformFormDataToPayload({
      goodsReceiptId: goodsReceipt.goodsReceiptId,
      goodsReceipt: goodsReceipt,
      grCostBreakdown,
      productsCostBreakdown,
      isEditMode,
    })

    const success = await upsertGrCostBreakdown(input)

    if (success) {
      handleCancelAllDialogs()
      onSuccess()
    }
  }

  const handleCancelConfirm = () => {
    handleCancelAllDialogs()
  }

  const handleCancelCancel = () => {
    if (productsCostBreakdown) {
      setCurrentDialog(CostBreakdownDialogType.ProductsCostBreakdown)
    } else {
      setCurrentDialog(CostBreakdownDialogType.GrCostBreakdown)
    }
  }

  const handleCancelAllDialogs = () => {
    setCurrentDialog(null)
    setGrCostBreakdown(undefined)
    setProductsCostBreakdown(undefined)
    setHasUserMadeAnyChanges(false) // Reset the change tracking
    onClose()
  }

  return (
    <>
      {currentDialog === CostBreakdownDialogType.GrCostBreakdown && (
        <CostBreakdownDialog
          goodsReceipt={goodsReceipt}
          grCostBreakdown={grCostBreakdown}
          onClose={handleCloseDialog}
          onSubmit={handleContinueToProducts}
        />
      )}

      {currentDialog === CostBreakdownDialogType.ProductsCostBreakdown && (
        <CostBreakdownProductsDialog
          goodsReceipt={goodsReceipt}
          grCostBreakdown={grCostBreakdown}
          productsCostBreakdown={productsCostBreakdown}
          purchaseOrderLineId={purchaseOrderLineId}
          isSubmitting={isLoading}
          onCancel={handleProductsCloseDialog}
          onBack={handleBackToGrCostBreakdown}
          onSubmit={handleFinalSubmit}
        />
      )}

      {currentDialog === CostBreakdownDialogType.Cancel && (
        <CancelDialog onClose={handleCancelCancel} onConfirm={handleCancelConfirm} />
      )}
    </>
  )
}
